import { Suspense, lazy, useMemo, useState, type ComponentType } from "react";
import { useConsole, PERIOD_LABELS } from "./ConsoleContext";
import { allowedRoutes, findRoute, GROUPS, subtitleOf, type RouteDef } from "./routes";
import { navigate, useHashLocation } from "./hashRoute";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { Icon } from "@/ui/Icon";
import { Skeleton } from "@/ui/states";
import { Chip } from "@/ui/Chip";
import { usePeriodPanel } from "./panels";

// Um componente lazy por rota, criado uma vez (o React exige identidade estável).
const lazyCache = new Map<string, ComponentType>();
function pageComponent(r: RouteDef): ComponentType {
  let c = lazyCache.get(r.slug);
  if (!c) {
    c = lazy(r.load);
    lazyCache.set(r.slug, c);
  }
  return c;
}

const MEDICAL = findRoute("auditoria-de-pareceres")!;
const ONBOARDING_SCREENS = new Set(["visao-geral", "conversas", "qualidade"]);

export function Shell() {
  const c = useConsole();
  const { slug } = useHashLocation();
  const routes = useMemo(() => allowedRoutes(c.me), [c.me]);
  const [menuOpen, setMenuOpen] = useState(false);
  const periodPanel = usePeriodPanel();

  const route = routes.find((r) => r.slug === slug) ?? routes[0];
  if (route && route.slug !== slug) queueMicrotask(() => navigate(route.slug));
  if (!route) return <div className="empty">Seu perfil não tem telas disponíveis.</div>;

  // Auditor (ou unidade de auditoria) abre a Visão geral direto na auditoria de pareceres, como no protótipo.
  const body =
    route.slug === "visao-geral" && (c.me.role === "Auditor do cliente" || c.scope.unit === "auditoria") ? MEDICAL : route;
  const Page = pageComponent(body);
  const onboarding = c.orgs.find((o) => o.id === c.scope.org)?.status === "onboarding" && ONBOARDING_SCREENS.has(route.slug);

  return (
    <>
      <Sidebar routes={routes} groups={GROUPS} current={route.slug} open={menuOpen} onNavigate={() => setMenuOpen(false)} />
      <div className="workspace">
        <Topbar onMenu={() => setMenuOpen((v) => !v)} />
        <main id="main">
          <div className="pagehead">
            <div>
              <div className="eyebrow">
                {c.orgName(c.scope.org)} <span style={{ margin: "0 6px" }}>/</span> {c.unitName(c.scope.unit)}
              </div>
              <h1>{route.label}</h1>
              <p>{subtitleOf(route)}</p>
            </div>
            <div className="datebar">
              <span className="update">
                <span className="dot" /> {route.live ? "Ao vivo" : "Atualizado até ontem 23:59"}
              </span>
              <button onClick={periodPanel}>
                <Icon name="calendar" /> {PERIOD_LABELS[c.scope.period]}
                <Icon name="chevron" />
              </button>
              <button aria-pressed={c.compare} onClick={() => c.setCompare(!c.compare)} title="Comparar com período anterior">
                <Icon name={c.compare ? "checked" : "square"} /> <span className="optional">Comparar</span>
              </button>
            </div>
          </div>
          {onboarding ? (
            <div className="card empty">
              <div className="score">✧</div>
              <h2>Um novo começo para o seu atendimento</h2>
              <p>Seus indicadores aparecerão após as primeiras conversas.</p>
              <Chip>Em homologação</Chip>
              <p>Lumi está sendo preparada para a Aurora Centro.</p>
            </div>
          ) : (
            <Suspense fallback={<Skeleton height={140} count={3} />}>
              <Page />
            </Suspense>
          )}
        </main>
        <footer>
          iuvoai · Console <span>Ambiente demonstrativo · Pessoas e dados fictícios</span>
        </footer>
      </div>
    </>
  );
}
